import React, { useCallback } from 'react';
import styled from 'styled-components';

// all chat imports should come from index for testing
import {
    ChatWidget,
    ClientStorage,
    ConnectionDetails,
    ManagedStatus,
    startPublicChat,
    startRingChat,
} from '@amzn/ring-cs-chatbot-widget';

const safeWindow = typeof window !== 'undefined' ? window : undefined;

/**
 * Example Parent Wrapper
 * position our ChatWrapper wherever you would like, below is a very basic example using just padding
 * NOTE: we override positioning of the chat widget and icon for mobile view
 */
const ParentWrapper = styled.div`
    height: 0px;
    width: 0px;

    position: fixed;
    bottom: 24px;
    right: 24px;

    /* account for the 1002 z-index set by AccountService Header, which only becomes a concern for our mobile experience */
    @media (max-width: 500px) {
        z-index: 1005;
    }
`;

const executePrompt = async (): Promise<undefined> => {
    let identityId = '';
    let token = '';

    // ensure prompt(s) re-populate if you leave window for any reason while entering credentials
    while (!identityId) {
        identityId = window.prompt('Please enter your identityId. To start a guest session, type \'guest\':') || '';
    }

    // Reset any previous errors before validation
    if (identityId === 'guest') {
        const searchParams = new URLSearchParams(window.location.search);
        const campaign = searchParams.get('campaign') || 'ACEC_US';
        const market = searchParams.get('market') || 'en-US';
        const resumeToken = searchParams.get('resumeToken') || undefined;

        const params = {
            campaign,
            market,
            resumeToken,
            enableSurvey: true, // Always enable Survey
            enableAgents: 'SHERLOCK',
        };
        console.log('calling startPublicChat with', params);

        // Call startPublicChat with campaign and resumeToken (undefined if not present)
        await startPublicChat(params);
    } else if (identityId !== 'guest') {
        while (!token) {
            token = window.prompt('Please enter your token:') || '';
        }

        const attributes = {
            enableAgents: 'SHERLOCK', // Always enable Sherlock
            enableSurvey: 'true',   // Always enable Survey
            clientStorage: 'memory', // use memory storage for authed sessions
        };

        const details: ConnectionDetails = { identityId, token, attributes };
        console.log('calling startRingChat with', details);

        // Authenticated user: start Ring chat
        await startRingChat(details);
    }
};

const handleStartChat = async () => {
    await executePrompt();
};

// FOR DEMO USE ONLY
export function WidgetTest () {
    const handleStatusChange = useCallback((status: ManagedStatus) => {
        console.log('Chat status:', status);
    }, []);

    return (
        <ParentWrapper>
            <ChatWidget
                chatOrigin={safeWindow?.location.origin}
                active={true}
                show={true}
                statusHandler={handleStatusChange}
                startHandler={handleStartChat}
                clientStorage={new URLSearchParams(safeWindow?.location.search).get('clientStorage') === ClientStorage.MEMORY ?
                    ClientStorage.MEMORY : ClientStorage.LOCAL_STORAGE}
            />
        </ParentWrapper>
    );
}
